import { useState } from "react";
import { 
  CircleStackIcon, 
  ServerIcon, 
  CogIcon, 
  DocumentTextIcon 
} from "@heroicons/react/24/outline";
import { Button } from "@/components";
import type { ProjectSchema } from "@/features/Projects/models/project.types";

interface SchemaTabsProps {
  schema: ProjectSchema;
  onGenerateModelsWithAI: () => void;
  onGenerateEndpointsWithAI: () => void;
  onAddModel: () => void;
  onAddEndpoint: () => void;
  onEditModel: (model: any) => void;
  onEditEndpoint: (endpoint: any) => void;
  onDeleteModel: (modelId: string) => void;
  onDeleteEndpoint: (endpointId: string) => void;
  showModelDeleteConfirm: string | null;
  setShowModelDeleteConfirm: (id: string | null) => void;
  showEndpointDeleteConfirm: string | null;
  setShowEndpointDeleteConfirm: (id: string | null) => void;
  isGeneratingModels: boolean;
  isGeneratingEndpoints: boolean;
  isAnyGenerating: boolean;
  isEditing: boolean;
}

type TabId = 'models' | 'endpoints' | 'config' | 'docs';

const tabs = [
  {
    id: 'models' as TabId,
    label: 'Modelos',
    icon: CircleStackIcon,
    color: 'blue'
  },
  {
    id: 'endpoints' as TabId,
    label: 'Endpoints',
    icon: ServerIcon,
    color: 'green'
  },
  {
    id: 'config' as TabId,
    label: 'Configurações',
    icon: CogIcon,
    color: 'purple'
  },
  {
    id: 'docs' as TabId,
    label: 'Documentação',
    icon: DocumentTextIcon,
    color: 'orange'
  }
];

export function SchemaTabs({ 
  schema,
  onGenerateModelsWithAI,
  onGenerateEndpointsWithAI,
  onAddModel,
  onAddEndpoint,
  onEditModel,
  onEditEndpoint,
  onDeleteModel,
  onDeleteEndpoint,
  showModelDeleteConfirm,
  setShowModelDeleteConfirm,
  showEndpointDeleteConfirm,
  setShowEndpointDeleteConfirm,
  isGeneratingModels,
  isGeneratingEndpoints,
  isAnyGenerating,
  isEditing
}: SchemaTabsProps) {
  const [activeTab, setActiveTab] = useState<TabId>('models');

  const getTabCount = (tabId: TabId) => {
    switch (tabId) {
      case 'models': return schema.dataModels.length;
      case 'endpoints': return schema.apiEndpoints.length;
      default: return null;
    }
  };

  const getTabColorClasses = (tabId: TabId, isActive: boolean) => {
    const tab = tabs.find(t => t.id === tabId);
    const color = tab?.color || 'gray';
    
    if (isActive) {
      return {
        button: `border-${color}-500 text-${color}-600`,
        icon: `text-${color}-600`,
        badge: `bg-${color}-100 text-${color}-800`
      };
    }
    
    return {
      button: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
      icon: 'text-gray-400',
      badge: 'bg-gray-100 text-gray-600'
    };
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'models':
        return (
          <ModelsTabContent
            schema={schema}
            onGenerateWithAI={onGenerateModelsWithAI}
            onAdd={onAddModel}
            onEdit={onEditModel}
            onDelete={onDeleteModel}
            showDeleteConfirm={showModelDeleteConfirm}
            setShowDeleteConfirm={setShowModelDeleteConfirm}
            isGenerating={isGeneratingModels}
            isAnyGenerating={isAnyGenerating}
          />
        );
      
      case 'endpoints':
        return (
          <EndpointsTabContent
            schema={schema}
            onGenerateWithAI={onGenerateEndpointsWithAI}
            onAdd={onAddEndpoint}
            onEdit={onEditEndpoint}
            onDelete={onDeleteEndpoint}
            showDeleteConfirm={showEndpointDeleteConfirm}
            setShowDeleteConfirm={setShowEndpointDeleteConfirm}
            isGenerating={isGeneratingEndpoints}
            isAnyGenerating={isAnyGenerating}
            isEditing={isEditing}
          />
        );
      
      case 'config':
        return <ConfigTabContent schema={schema} />;
      
      case 'docs':
        return <DocsTabContent schema={schema} />;
      
      default:
        return null;
    }
  };

  return (
    <div className="w-full">
      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const count = getTabCount(tab.id);
            const isActive = activeTab === tab.id;
            const classes = getTabColorClasses(tab.id, isActive);
            const Icon = tab.icon;

            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors ${classes.button}`}
              >
                <Icon className={`-ml-0.5 mr-2 h-5 w-5 transition-colors ${classes.icon}`} />
                {tab.label}
                {count !== null && (
                  <span className={`ml-2 py-0.5 px-2.5 rounded-full text-xs font-medium transition-colors ${classes.badge}`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {renderTabContent()}
      </div>
    </div>
  );
}

// Componente para conteúdo da aba de Modelos
function ModelsTabContent({ 
  schema, 
  onGenerateWithAI, 
  onAdd, 
  onEdit, 
  onDelete,
  showDeleteConfirm,
  setShowDeleteConfirm,
  isGenerating, 
  isAnyGenerating 
}: any) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="space-y-4">
      {/* Overview Card */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Modelos de Dados</h3>
            <p className="text-sm text-gray-500">
              {schema.dataModels.length} modelos • {schema.stats.totalFields} campos totais
            </p>
          </div>
          <div className="flex space-x-2">
            <Button
              onClick={onGenerateWithAI}
              disabled={isGenerating}
              leftIcon={<CircleStackIcon className="h-4 w-4" />}
              size="sm"
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
            >
              {isGenerating ? "Gerando..." : "Gerar com IA"}
            </Button>
            <Button
              onClick={onAdd}
              variant="outline"
              size="sm"
            >
              + Novo Modelo
            </Button>
          </div>
        </div>

        {/* Models Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          {schema.dataModels.slice(0, 3).map((model: any) => (
            <div key={model.id} className="p-3 bg-gray-50 rounded-lg">
              <div className="font-medium text-gray-900">{model.name}</div>
              <div className="text-sm text-gray-500">
                {model.fields.length} campos • {model.tableName}
              </div>
              <div className="mt-2 flex space-x-2">
                <button
                  onClick={() => onEdit(model)}
                  className="text-xs text-blue-600 hover:text-blue-700"
                >
                  Editar
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(model.id)}
                  className="text-xs text-red-600 hover:text-red-700"
                >
                  Excluir
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Expand/Collapse */}
        {schema.dataModels.length > 3 && (
          <div className="text-center">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              {isExpanded ? "Ver menos" : `Ver todos (${schema.dataModels.length - 3} mais)`}
            </button>
          </div>
        )}

        {/* Expanded List */}
        {isExpanded && schema.dataModels.length > 3 && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            {schema.dataModels.slice(3).map((model: any) => (
              <div key={model.id} className="p-3 bg-gray-50 rounded-lg">
                <div className="font-medium text-gray-900">{model.name}</div>
                <div className="text-sm text-gray-500">
                  {model.fields.length} campos • {model.tableName}
                </div>
                <div className="mt-2 flex space-x-2">
                  <button
                    onClick={() => onEdit(model)}
                    className="text-xs text-blue-600 hover:text-blue-700"
                  >
                    Editar
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(model.id)}
                    className="text-xs text-red-600 hover:text-red-700"
                  >
                    Excluir
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-800 mb-3">
            Tem certeza que deseja excluir este modelo?
          </p>
          <div className="flex space-x-2">
            <Button
              onClick={() => onDelete(showDeleteConfirm)}
              variant="danger"
              size="sm"
            >
              Sim, excluir
            </Button>
            <Button
              onClick={() => setShowDeleteConfirm(null)}
              variant="outline"
              size="sm"
            >
              Cancelar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// Componente para conteúdo da aba de Endpoints
function EndpointsTabContent({ 
  schema, 
  onGenerateWithAI, 
  onAdd, 
  onEdit, 
  onDelete,
  showDeleteConfirm,
  setShowDeleteConfirm,
  isGenerating, 
  isAnyGenerating,
  isEditing 
}: any) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [groupBy, setGroupBy] = useState<'method' | 'tag'>('tag');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const toggleGroupExpansion = (groupName: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupName)) {
        newSet.delete(groupName);
      } else {
        newSet.add(groupName);
      }
      return newSet;
    });
  };

  const getMethodStyles = (method: string) => {
    switch (method) {
      case "GET": return "bg-green-100 text-green-800 border-green-200";
      case "POST": return "bg-blue-100 text-blue-800 border-blue-200";
      case "PUT": return "bg-orange-100 text-orange-800 border-orange-200";
      case "PATCH": return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "DELETE": return "bg-red-100 text-red-800 border-red-200";
      default: return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const groupedEndpoints = schema.apiEndpoints.reduce((acc: any, endpoint: any) => {
    const key = groupBy === 'method' ? endpoint.method : endpoint.tags[0] || 'Outros';
    if (!acc[key]) acc[key] = [];
    acc[key].push(endpoint);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      {/* Overview Card */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Endpoints da API</h3>
            <p className="text-sm text-gray-500">
              {schema.apiEndpoints.length} endpoints • REST API
            </p>
          </div>
          <div className="flex space-x-2">
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value as 'method' | 'tag')}
              className="text-sm border border-gray-300 rounded-md px-3 py-1 pr-8 min-w-[140px] appearance-none bg-white bg-no-repeat bg-right"
              style={{
                backgroundImage: `url("data:image/svg+xml;charset=utf-8,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                backgroundPosition: 'right 0.5rem center',
                backgroundSize: '1.5em 1.5em'
              }}
            >
              <option value="tag">Agrupar por Tag</option>
              <option value="method">Agrupar por Método</option>
            </select>
            <Button
              onClick={onGenerateWithAI}
              disabled={isGenerating}
              leftIcon={<ServerIcon className="h-4 w-4" />}
              size="sm"
              className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
            >
              {isGenerating ? "Gerando..." : "Gerar com IA"}
            </Button>
            <Button
              onClick={onAdd}
              variant="outline"
              size="sm"
            >
              + Novo Endpoint
            </Button>
          </div>
        </div>

        {/* Method Distribution */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-6">
          {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map(method => {
            const count = schema.apiEndpoints.filter((e: any) => e.method === method).length;
            return (
              <div key={method} className="text-center p-2 bg-gray-50 rounded">
                <div className={`inline-block px-2 py-1 rounded text-xs font-medium ${getMethodStyles(method)}`}>
                  {method}
                </div>
                <div className="text-sm text-gray-600 mt-1">{count}</div>
              </div>
            );
          })}
        </div>

        {/* Grouped Endpoints */}
        <div className="space-y-4">
          {Object.entries(groupedEndpoints).slice(0, isExpanded ? undefined : 2).map(([group, endpoints]: [string, any]) => {
            const isGroupExpanded = expandedGroups.has(group);
            const visibleEndpoints = isGroupExpanded ? endpoints : endpoints.slice(0, 3);
            
            return (
              <div key={group} className="border border-gray-200 rounded-lg">
                <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                  <h4 className="font-medium text-gray-900">{group}</h4>
                  <p className="text-sm text-gray-500">{endpoints.length} endpoints</p>
                </div>
                <div className="p-4 space-y-2">
                  {visibleEndpoints.map((endpoint: any) => (
                    <div key={endpoint.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div className="flex items-center space-x-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getMethodStyles(endpoint.method)}`}>
                          {endpoint.method}
                        </span>
                        <div>
                          <div className="font-medium text-gray-900">{endpoint.path}</div>
                          <div className="text-sm text-gray-500">{endpoint.summary}</div>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => onEdit(endpoint)}
                          className="text-xs text-blue-600 hover:text-blue-700"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(endpoint.id)}
                          className="text-xs text-red-600 hover:text-red-700"
                        >
                          Excluir
                        </button>
                      </div>
                    </div>
                  ))}
                  {endpoints.length > 3 && (
                    <div className="text-center py-2">
                      <button
                        onClick={() => toggleGroupExpansion(group)}
                        className="text-sm text-green-600 hover:text-green-700 font-medium"
                      >
                        {isGroupExpanded 
                          ? "Ver menos" 
                          : `+${endpoints.length - 3} endpoints mais`
                        }
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Expand/Collapse */}
        {Object.keys(groupedEndpoints).length > 2 && (
          <div className="text-center mt-4">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-sm text-green-600 hover:text-green-700 font-medium"
            >
              {isExpanded ? "Ver menos" : `Ver todos (${Object.keys(groupedEndpoints).length - 2} grupos mais)`}
            </button>
          </div>
        )}
      </div>

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-800 mb-3">
            Tem certeza que deseja excluir este endpoint?
          </p>
          <div className="flex space-x-2">
            <Button
              onClick={() => onDelete(showDeleteConfirm)}
              variant="danger"
              size="sm"
            >
              Sim, excluir
            </Button>
            <Button
              onClick={() => setShowDeleteConfirm(null)}
              variant="outline"
              size="sm"
            >
              Cancelar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function ConfigTabContent({ schema }: any) {
  return (
    <div>
      {/* Importar e usar o ConfigurationSection existente */}
      <ConfigurationSectionContent schema={schema} />
    </div>
  );
}

function DocsTabContent({ schema }: any) {
  return (
    <div>
      {/* Importar e usar o DocumentationSection existente */}
      <DocumentationSectionContent schema={schema} />
    </div>
  );
}

// Componentes inline para evitar imports circulares por agora
function ConfigurationSectionContent({ schema }: any) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <CogIcon className="h-5 w-5 text-cyan-600" />
          <div>
            <h4 className="font-medium text-gray-900">Configurações Técnicas</h4>
            <p className="text-xs text-gray-500">Informações para desenvolvedores</p>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {/* API Configuration */}
        <div>
          <div className="flex items-center space-x-2 mb-3">
            <ServerIcon className="h-4 w-4 text-blue-600" />
            <h5 className="font-medium text-gray-900">Configuração da API</h5>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 ml-6">
            <div>
              <div className="text-sm font-medium text-gray-700">Versão da API</div>
              <div className="mt-1 text-sm text-gray-900 font-mono">{schema.config.apiVersion}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-700">Base URL</div>
              <div className="mt-1 text-sm text-gray-900 font-mono break-all">
                {schema.config.baseUrl}
              </div>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-700">Estilo</div>
              <div className="mt-1">
                <span className="px-2 py-1 text-xs font-medium bg-indigo-100 text-indigo-800 rounded">
                  REST API
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Database Configuration */}
        <div>
          <div className="flex items-center space-x-2 mb-3">
            <CircleStackIcon className="h-4 w-4 text-green-600" />
            <h5 className="font-medium text-gray-900">Banco de Dados</h5>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 ml-6">
            <div>
              <div className="text-sm font-medium text-gray-700">Tipo</div>
              <div className="mt-1">
                <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded">
                  {schema.config.databaseType}
                </span>
              </div>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-700">Modelos</div>
              <div className="mt-1 text-sm text-gray-900">{schema.stats.totalModels} tabelas</div>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-700">Campos</div>
              <div className="mt-1 text-sm text-gray-900">{schema.stats.totalFields} campos</div>
            </div>
          </div>
        </div>

        {/* Security Configuration */}
        <div>
          <div className="flex items-center space-x-2 mb-3">
            <span className="h-4 w-4 text-orange-600">🔒</span>
            <h5 className="font-medium text-gray-900">Segurança</h5>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 ml-6">
            <div>
              <div className="text-sm font-medium text-gray-700">Autenticação</div>
              <div className="mt-1">
                <span className="px-2 py-1 text-xs font-medium bg-orange-100 text-orange-800 rounded">
                  {schema.config.authenticationMethod.toUpperCase()}
                </span>
              </div>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-700">CORS</div>
              <div className="mt-1">
                <span className={`px-2 py-1 text-xs font-medium rounded ${
                  schema.config.corsEnabled 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {schema.config.corsEnabled ? 'Habilitado' : 'Desabilitado'}
                </span>
              </div>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-700">Rate Limit</div>
              <div className="mt-1">
                <span className={`px-2 py-1 text-xs font-medium rounded ${
                  schema.config.rateLimitEnabled 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {schema.config.rateLimitEnabled ? 'Habilitado' : 'Desabilitado'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DocumentationSectionContent({ schema }: any) {
  const downloadSwagger = () => {
    // Função simplificada para download
    const yamlContent = `openapi: 3.0.0
info:
  title: ${schema.config.openApiInfo.title}
  description: ${schema.config.openApiInfo.description}
  version: ${schema.config.openApiInfo.version}

# ${schema.stats.totalEndpoints} endpoints
# ${schema.stats.totalModels} modelos
# Gerado automaticamente`;

    const blob = new Blob([yamlContent], { type: 'text/yaml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${schema.config.openApiInfo.title.toLowerCase().replace(/\s+/g, '-')}-api.yaml`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <DocumentTextIcon className="h-5 w-5 text-cyan-600" />
          <div>
            <h4 className="font-medium text-gray-900">Documentação do Negócio</h4>
            <p className="text-xs text-gray-500">Informações para stakeholders</p>
          </div>
        </div>
        <Button
          onClick={downloadSwagger}
          leftIcon={<span>📥</span>}
          size="sm"
          variant="outline"
        >
          Download Swagger
        </Button>
      </div>

      <div className="space-y-6">
        {/* API Overview */}
        <div>
          <div className="flex items-center space-x-2 mb-3">
            <span className="h-4 w-4 text-blue-600">🌐</span>
            <h5 className="font-medium text-gray-900">Visão Geral da API</h5>
          </div>
          <div className="ml-6 p-4 bg-gray-50 rounded-lg">
            <h6 className="font-medium text-gray-900 mb-2">
              {schema.config.openApiInfo.title}
            </h6>
            <p className="text-sm text-gray-700 mb-3">
              {schema.config.openApiInfo.description}
            </p>
            <div className="flex items-center space-x-4 text-xs text-gray-500">
              <span>Versão: {schema.config.openApiInfo.version}</span>
              <span>•</span>
              <span>{schema.stats.totalEndpoints} endpoints</span>
              <span>•</span>
              <span>{schema.stats.totalModels} modelos</span>
            </div>
          </div>
        </div>

        {/* Business Purpose */}
        <div>
          <div className="flex items-center space-x-2 mb-3">
            <span className="h-4 w-4 text-yellow-600">💡</span>
            <h5 className="font-medium text-gray-900">Propósito do Sistema</h5>
          </div>
          <div className="ml-6 p-4 bg-blue-50 border-l-4 border-blue-400">
            <h6 className="font-medium text-blue-900 mb-2">Objetivo Principal</h6>
            <p className="text-sm text-blue-800">
              Sistema desenvolvido para otimizar processos e melhorar a experiência do usuário 
              através de uma API moderna e bem estruturada.
            </p>
          </div>
        </div>

        {/* Users & Stakeholders */}
        <div>
          <div className="flex items-center space-x-2 mb-3">
            <span className="h-4 w-4 text-green-600">👥</span>
            <h5 className="font-medium text-gray-900">Usuários e Stakeholders</h5>
          </div>
          <div className="ml-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-3 border border-gray-200 rounded-lg">
              <h6 className="font-medium text-gray-900 mb-2">Desenvolvedores</h6>
              <p className="text-sm text-gray-600">
                Implementação, manutenção e integração da API
              </p>
            </div>
            <div className="p-3 border border-gray-200 rounded-lg">
              <h6 className="font-medium text-gray-900 mb-2">Usuários Finais</h6>
              <p className="text-sm text-gray-600">
                Interação através de aplicações client
              </p>
            </div>
            <div className="p-3 border border-gray-200 rounded-lg">
              <h6 className="font-medium text-gray-900 mb-2">Administradores</h6>
              <p className="text-sm text-gray-600">
                Monitoramento e gestão do sistema
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}